import { Box, Grid, Paper, Skeleton, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import dynamic from 'next/dynamic';
import React, { useMemo, useState } from 'react';
import useSWR from 'swr';
import _getPriceData from "../../../../../../pages/api/_getPriceData";
import { dataConstants } from '../../../../../constants';
import { noChartData } from '../../../../../helpers';
import { convertDate, getTradingItem } from '../../../../../helpers/time';
import { parseTabData } from '../../../../../utils/helpers';
import GrufityCircularProgress from '../../../../_common/GrufityProgress/CircularProgress';

const BrushChart = dynamic(() => import('./Widget/BrushChart'));

const { STOCK_SECTION_CODES } = dataConstants;

const getDate = (d) => d?._parsedDate || new Date(d?.recordDate);

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? 'rgb(10, 25, 41)' : '#fff',
  ...theme.typography.body2,
  position: 'relative',
  color: theme.palette.text.secondary,
  boxShadow: theme.palette.mode === 'dark' ? 0 : '0 0.5px 1px rgb(255 255 255 / 20%), 0 2px 3px rgb(255 255 255 / 10%)',
  border: theme.palette.mode === 'dark' ? '1px solid rgb(19, 47, 76)' : '1px solid #eae9ee',
  borderRadius: '7px',
  '& .Item-Head': {
    background: '#f2f2f2',
    borderBottom: '1px solid #eae9ee',
    padding: '0 8px 0 8px',
    borderRadius: '6px 6px 0 0',
    '& a': {
      textDecoration: 'none',
    },
    '& .option-title': {
      fontWeight: 500,
      color: '#333',
      fontSize: '.875rem',
    }
  },
  '& .Item-Body': {
    padding: theme.spacing(0, 0),
    paddingTop: 0,
  },
  '& .fst': { display: "none" },
  '&:hover .fst': { display: "block" },
  '& .skeleton:after': {
    animation: '2s linear 0.5s infinite normal none running animation-wiooy9',
    background: 'linear-gradient(90deg, transparent, rgb(0 0 0 / 9%), transparent)',
    content: "''",
    position: 'absolute',
    transform: 'translateX(-100%)',
    inset: '0px',
  }
}));

const getChartData = (chart, ticker) => {
  if (!chart) return null;

  const clonedChart = JSON.parse(JSON.stringify(chart));
  const { stock_data, series_data, sequenceData } = clonedChart;

  let code = stock_data && stock_data.length > 0 ? stock_data[0]?.code : undefined;
  code = !code && series_data && series_data.length > 0 ? series_data[0]?.code : code;
  code = !code && sequenceData && sequenceData.length > 0 ? sequenceData[0]?.code : code;

  clonedChart.series = clonedChart.series?.map(d => ({ 
    ...d, 
    yformula: d?.yformula?.replace(/XXXXX/g, ticker) 
  }));
  clonedChart.ySeries = clonedChart.ySeries?.replace(/XXXXX/g, ticker);
  
  return code ? { ...clonedChart } : clonedChart;
};

const processRSIData = (tabData, info) => {
  const findAndParseTabData = (sectionCode) => {
    const item = tabData?.find(d => d.section_code === sectionCode);
    return item ? parseTabData(item) : null;
  };

  const RSIChartItem = findAndParseTabData("rsi");
  const RSIChartSettings = RSIChartItem?.settings || {};
  const { dataToFilter = [] } = RSIChartSettings;

  const processedDataToFilter = dataToFilter.map(d => d.replace(/XXXXX/g, info?.ticker));

  const _RSIData = findAndParseTabData("ISSeries");
  const RSISeriesData = _RSIData?.tabData?.filter(d => processedDataToFilter.includes(d.code)) || [];

  const RSIData = RSISeriesData.map(item => ({
    recordDate: new Date(item.recordDate),
    value: item.value,
  })).sort((a, b) => b.recordDate - a.recordDate);

  return { RSIData };
};

const Lifestory = ({ info, data, tabData, ticker, themes, realtimeData, getTradingHours, desk, insider_data, fund_data, filings_data }) => {

  const { data: stock_price_data, isLoading } = useSWR(
    info?.ticker ? ["api/_getPriceData", info.ticker, "", "", false] : null,
    _getPriceData, {
    refreshInterval: 12 * 3600000,
    revalidateOnFocus: false,
    keepPreviousData: true,
    onErrorRetry: (error, key, config, revalidate, { retryCount }) => {
      if (error.status === 404) return;
      if (retryCount >= 10) return;
      setTimeout(() => revalidate({ retryCount }), 5000);
    }
  });

  let { getStockDataV2: stock_data } = stock_price_data || {};
  let { daily_stock_data } = data || {};
  const { realtime_stock_data } = info || {};

  const currDateTime = new Date(getTradingItem({ recordDate: new Date() }).recordDate);
  let lastEndTime = realtime_stock_data && realtime_stock_data.length > 0 ? new Date(getTradingItem({ recordDate: realtime_stock_data[0].record_date }).recordDate) : null;
  let lastStartTime = realtime_stock_data && realtime_stock_data.length > 0 ? new Date(getTradingItem({ recordDate: realtime_stock_data[0].record_date }).recordDate) : null;
  lastEndTime = lastEndTime ? new Date(convertDate(lastEndTime) + 'T' + getTradingHours.end_time) : null;
  lastStartTime = lastStartTime ? new Date(convertDate(lastStartTime) + 'T' + getTradingHours.start_time) : null;
  
  const isTrading = lastEndTime && lastStartTime && +lastEndTime > +currDateTime && +lastStartTime < +currDateTime;
  const todayEndTime = lastEndTime ? lastEndTime : null;

  lastEndTime = isTrading ? new Date(`${convertDate(new Date(realtime_stock_data[1].record_date))} ${getTradingHours.end_time}`) : lastEndTime;
  const prevClose = realtime_stock_data && realtime_stock_data.length > 1 ? realtime_stock_data[1] : null;

  let chart = useMemo(() => {
    const rawChart = tabData ? tabData.find(d => d.section_code === STOCK_SECTION_CODES.LIFESTORY) : null;
    return rawChart ? parseTabData(rawChart)?.tabData : null;
  }, [tabData]);

  const convertedStockData = useMemo(() => {
    return stock_data?.map(item => {
      const utcDate = new Date(item.recordDate);
      const newYorkDate = new Date(utcDate.toLocaleString('en-US', { timeZone: 'America/New_York' }));
      return {
        ...item,
        recordDate: newYorkDate,
        _parsedDate: newYorkDate
      };
    });
  }, [stock_data]);

  let chartData = useMemo(() => {
    if (!chart) return null;
    const sortedStockData = convertedStockData?.sort((a, b) => getDate(a) - getDate(b));
    const processedChart = getChartData({
      ...chart,
      stock_data: sortedStockData,
      dailyStockData: daily_stock_data,
    }, ticker);

    if (processedChart?.series?.[0]?.setting) {
      processedChart.series[0].setting.area.fill = "#008000";
      processedChart.series[0].setting.line.stroke = "#008000";
    }

    return processedChart;
  }, [chart, convertedStockData, daily_stock_data, ticker]);

  const { RSIData } = useMemo(() => processRSIData(tabData, info), [tabData, info]);
  const insiderData = insider_data?.tabData?.length > 0 ? insider_data.tabData : null;
  const fundData = fund_data?.tabData?.length > 0 ? fund_data.tabData : null;

  if (isLoading && noChartData(chartData)) {
    return <Grid size={{ xs: 12, sm: 12, md: 12 }}><Skeleton variant="rounded" width={'100%'} height={319} /></Grid>;
  }

  return (
    <>
      {chartData && chartData?.series && !noChartData(chartData) &&
        <Grid size={{ md: 12, xs: 12 }}>
          <Item className="s-card life-story-chart" id="docEl">
            <Box className="crisischart">
              <Box className="Item-Head" sx={{ display: 'flex', alignItems: 'flex-start', px: 0.5 }}>
                <Typography component="h2" className="option-title" sx={{ mr: 'auto' }} >
                  {`${ticker} Stock Price`}
                </Typography>
              </Box>
              <Box component="div" className="option-content Item-Body">
                <Box component="div" className="life-story-chart" sx={{
                  minHeight: { md: '18rem', xs: '13rem' }, position: "relative",
                  '& .widget-chart': {
                    width: '100%',
                    height: 'auto',
                    filter: isLoading ? 'blur(6px)' : 'blur(0px)',
                  },
                }}>
                  {isLoading ? (
                    <Box component="div" sx={{ position: 'absolute', top: '50%', left: '50%', zIndex: 3, transform: 'translate(-50%, -50%)', color: 'primary.main' }}>
                      <GrufityCircularProgress size={15} />
                    </Box>
                  ) : null}
                  <BrushChart
                    ticker={ticker}
                    chartData={{
                      ...chartData,
                      prevClose: prevClose || (realtime_stock_data?.length > 0 ? realtime_stock_data[0] : null),
                      trading: { isTrading, todayEndTime },
                    }}
                    width={500}
                    height={desk ? 375 : 220}
                    insiderData={insiderData}
                    fundData={fundData}
                    rsiData={RSIData}
                    secFilings={filings_data}
                  />
                </Box>
              </Box>
            </Box>
          </Item>
        </Grid>
      }
    </>
  );
};

export default React.memo(Lifestory);